import { useState, useEffect, useMemo } from "react";
import { getChatContexts, type ChatContextConfig } from "@/services/api/chat-contexts";
import { defaultChatContextConfigs } from "@/config/chatContexts";
import type { AniChatWidgetContextValue } from "@/context/AniContext";
import { useAuth } from "@/context/AuthContext";

export function useChatContextConfig(context: AniChatWidgetContextValue) {
    const [companyConfigs, setCompanyConfigs] = useState<Record<string, ChatContextConfig> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();

    useEffect(() => {
        async function fetchConfigs() {
            // Only fetch if user has a company
            if (!user?.company_id) {
                setLoading(false);
                return;
            }

            try {
                const response = await getChatContexts();
                setCompanyConfigs(response.contexts);
                setError(null);
            } catch (err) {
                console.error("Failed to fetch chat context configs:", err);
                setError("Failed to load chat preferences");
                // Fall back to defaults on error
                setCompanyConfigs(null);
            } finally {
                setLoading(false);
            }
        }

        fetchConfigs();
    }, [user?.company_id]);

    // Merge company-specific config with defaults
    const config = useMemo(() => {
        if (!context) {
            // If no context is provided, return a default inactive state
            return {
                isActive: false,
                initialAssistantMessage: "Ani coming soon",
                samplePrompts: []
            };
        }

        const defaultConfig = defaultChatContextConfigs[context];
        const companyConfig = companyConfigs?.[context];

        // If we have company-specific config, use it; otherwise use defaults
        if (companyConfig) {
            return {
                isActive: companyConfig.is_active,
                initialAssistantMessage: companyConfig.initial_assistant_message,
                samplePrompts: companyConfig.sample_prompts
            };
        }

        // If no default config exists for this context, return inactive state
        if (!defaultConfig) {
            return {
                isActive: false,
                initialAssistantMessage: "Ani coming soon",
                samplePrompts: []
            };
        }

        return defaultConfig;
    }, [context, companyConfigs]);

    return {
        config,
        loading,
        error,
        isActive: config?.isActive ?? false,
        initialAssistantMessage: config?.initialAssistantMessage ?? "",
        samplePrompts: config?.samplePrompts ?? []
    };
} 